import { reduce, isEmpty, get, chain, isFunction } from "lodash"
import { orderMetricMetadata } from "helpers/metrics/utils"
import { buildContributorKey } from "helpers/contributors"

export const ROWS_PER_PAGE = 200

export const SORT_DIRECTIONS = {
  ASC: "asc",
  DESC: "desc",
  ASC_ABS: "asc_abs",
  DESC_ABS: "desc_abs",
}

export const TOOLTIP_MIN_HEADER_LENGTH = 10
export const DEFAULT_PRIORITY_ORDER = 0
export const AGGREGATION_PRIORITY_ORDER = -1

// Default tooltip methods for metrics
const createDefaultTooltipMethods = (metric) => ({
  shouldShowTooltip: (fallbackHeaderName) => {
    const hasExplicitTooltip = metric.tooltipText || metric.adjustedReason
    return hasExplicitTooltip || (fallbackHeaderName && fallbackHeaderName.length > TOOLTIP_MIN_HEADER_LENGTH)
  },

  getTooltipText: (fallbackHeaderName) => {
    return metric.tooltipText || metric.adjustedReason || fallbackHeaderName
  },
})

// Note: orderMetricMetadata is now imported from helpers/metrics/utils to avoid duplication
// Re-exported here to avoid modifying the import path in other files
export { orderMetricMetadata }

export function makeMetricMetadataAndCategories(metrics, data, disableCategories = false) {
  const newMetrics = isFunction(metrics) ? metrics(metrics) : metrics
  if (!newMetrics) {
    return { metricCategories: {}, metricMetadata: [] }
  }
  return reduce(metrics, ({ metricCategories, metricMetadata }, metric) => {
    if (metric.downloadOnly) {
      return { metricCategories, metricMetadata }
    }

    const hasTopLevelMetricData = metric.dataGetter ? metric.dataGetter(data?.values) : get(data, `values.${metric.dataPath}`)

    const hasMetricData = hasTopLevelMetricData || chain(data)
      .get("children")
      .some(child => metric.dataGetter ? metric.dataGetter(child.values) : get(child, `values.${metric.dataPath}`))
      .value()

    if (!hasMetricData && metric.hideIfEmpty) {
      return { metricCategories, metricMetadata }
    }

    metricCategories[metric.category] = metricCategories[metric.category] || { label: metric.category, nbMetrics: 0 }
    const enhancedMetric = {
      ...metric,
      metricIndex: metricCategories[metric.category].nbMetrics,
      isSticky: !disableCategories && !metric.category,
      ...createDefaultTooltipMethods(metric),
    }
    metricMetadata.push(enhancedMetric)
    metricCategories[metric.category].nbMetrics += 1
    return { metricCategories, metricMetadata }
  }, { metricCategories: {}, metricMetadata: [] })
}

export function getTreeLevelData(treeData, { levelsUp = 0, path = [] }) {
  // Handle root level as a special case
  if (isEmpty(path)) {
    return levelsUp === 0 ? treeData : {}
  }

  // If requesting current level, no need to traverse up
  if (levelsUp === 0) {
    return navigateToNode(treeData, path)
  }

  // Get path to ancestor by removing last N elements
  const ancestorPath = path.slice(0, -levelsUp)
  return navigateToNode(treeData, ancestorPath)
}

function navigateToNode(root, path) {
  return path.reduce((node, id) => node?.children?.[id], root)
}

export function checkIsHighlighted(contributor, highlightedRowIds) {
  if (isEmpty(highlightedRowIds)) {
    return false
  }

  if (highlightedRowIds.includes(buildContributorKey(contributor))) {
    return true
  }

  if (contributor.children) {
    return Object.entries(contributor.children).some(([key, child]) => checkIsHighlighted({ ...child, id: key }, highlightedRowIds))
  }

  return false
}
